import React, { useState } from "react";
import {
  minusOnePx,
  Modal,
  ModalProps,
  styled,
  useMedia,
  useTheme,
} from "@catchoftheday/cg-components";
import {
  SOURCE_TYPE_CATCH_PRODUCT_RECOMMENDER,
  emitRecommenderRequest,
} from "@catchoftheday/analytics";
import { keyframes } from "@emotion/react";
import { useEventBus } from "@catchoftheday/state";
import { MiniCartItem } from "@catchoftheday/header-app";
import { getProductTiles } from "@catchoftheday/product-app";
import {
  useSellerRecommendedProductsQuery,
  SellerRecommendedProductsQueryVariables,
} from "@common-vendors/generated/graphql";
import { BaseRecommenderCarousel, getRecommenderTrackingData } from "..";
import { TRIGGER_ONEPASS_FREE_SHIPPING_DRAWER_EVENT } from "./trigger";
import { ModalContent } from "./ModalContent";

const ONE_PASS_PIPUP_RECOMMENDER_SOURCE_ID = "OnePass Popup Recommender";

const slide = keyframes`
  from {
    transform: translateY(150%);
  }

  to {
    transform: translateY(0%);
  }
`;

export type FreeShippingDrawerProps = {
  productId: number;
  offerId?: number;
  price?: number;
  sellerId?: number;
  isAdult?: boolean;
  cartItems?: MiniCartItem[];
};

const FreeShippingModal = styled(Modal)<ModalProps>`
  .ModalContent {
    position: absolute;
    bottom: 0;
    z-index: 5;
    width: 100%;
    height: unset;
    overflow: hidden;
    position: absolute;
    animation: ${slide} 1s ease-in-out;
    box-shadow: 0 0 5px black;
    border-radius: 0px;
    border: none;
    padding: 0px;
  }
`;

export const FreeShippingDrawer = () => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [eventPayload, setEventPayload] = useState<
    SellerRecommendedProductsQueryVariables & {
      price: number;
      offerId: number;
    }
  >();

  const fromProduct = {
    from_product_ids: eventPayload?.productId
      ? [Number(eventPayload.productId)]
      : null,
    from_product_offer_id: eventPayload?.offerId || null,
    from_product_displayed_price: eventPayload?.price || null,
  };
  const { data } = useSellerRecommendedProductsQuery({
    skip: !eventPayload,
    variables: eventPayload
      ? {
          productId: eventPayload.productId,
          excludedProductIds: eventPayload.excludedProductIds,
          sellerId: eventPayload.sellerId,
          includeAdultCategory: eventPayload.includeAdultCategory,
        }
      : {},
    ssr: false,
    onCompleted: ({ sellerRecommendedProducts: { metaData } }) => {
      emitRecommenderRequest({
        source_type: SOURCE_TYPE_CATCH_PRODUCT_RECOMMENDER,
        pre_hydration_product_count: metaData?.preHydrationCount || null,
        post_hydration_product_count: metaData?.postHydrationCount || null,
        hydration_error_count: metaData?.hydrationErrorCount || null,
        ...fromProduct,
      });
    },
  });
  const recommendedProducts = data?.sellerRecommendedProducts.products;

  const isMobile = useMedia<boolean>(
    [`(max-width: ${minusOnePx(theme.breakpoints.md, true)})`],
    [true]
  );

  useEventBus<FreeShippingDrawerProps>(
    TRIGGER_ONEPASS_FREE_SHIPPING_DRAWER_EVENT,
    async (event) => {
      if (event.payload) {
        const { productId, sellerId, cartItems, isAdult, offerId, price } =
          event.payload;

        const excludedProductIds = (cartItems || [])
          .filter((item) => !!item.productId)
          .map((item) => item.productId as string);

        setEventPayload({
          productId: String(productId),
          excludedProductIds,
          sellerId,
          includeAdultCategory: !!isAdult,
          offerId: Number(offerId),
          price: Number(price),
        });

        setIsOpen(true);
      }
    }
  );

  if (!recommendedProducts) {
    return null;
  }

  const productTiles = getProductTiles({
    products: recommendedProducts,
    sourceId: ONE_PASS_PIPUP_RECOMMENDER_SOURCE_ID,
    sourceType: SOURCE_TYPE_CATCH_PRODUCT_RECOMMENDER,
    fromProduct,
  });

  return (
    <FreeShippingModal
      isOpen={isOpen}
      contentProps={{
        paddingX: 0,
        paddingY: 0,
        background: theme.colors.grey,
      }}
      onRequestClose={() => setIsOpen(false)}
    >
      <ModalContent>
        <BaseRecommenderCarousel
          trackingData={getRecommenderTrackingData(
            recommendedProducts,
            "product"
          )}
          sourceType={SOURCE_TYPE_CATCH_PRODUCT_RECOMMENDER}
          fromProduct={fromProduct}
          sourceId={ONE_PASS_PIPUP_RECOMMENDER_SOURCE_ID}
          carouselProps={{
            showNavButtons: !isMobile,
            buttonOffset: [0, 0],
            containerProps: { width: "100%" },
            slidesPerBreakpoint: [4, 3, 2, 2, 2],
            showBullets: !!isMobile,
          }}
          tiles={productTiles}
        />
      </ModalContent>
    </FreeShippingModal>
  );
};
